const isFinite = require('lodash/isFinite');
const get = require('lodash/get');
const defaults = require('lodash/defaults');

const numberFormat = require('underscore.string/numberFormat');
const currencies = require('./currencies').currencies;
const locales = require('./locales').locales;
const uoms = require('./locales').uoms;
const dayjs = require('dayjs');
const utc = require('dayjs/plugin/utc');
const localizedFormat = require('dayjs/plugin/localizedFormat');
dayjs.extend(utc);
dayjs.extend(localizedFormat);
require('dayjs/locale/en-au');
require('dayjs/locale/es');
require('dayjs/locale/fr');
require('dayjs/locale/it');
require('dayjs/locale/en-gb');
require('dayjs/locale/de');
require('dayjs/locale/nl');
require('dayjs/locale/en-ca');

const defaultLocale = {
  defaults: {
    dateFormat: 'LL',
    locale: 'en-US'
  }
};

const mappingLocales = {
  'de-DE': 'de',
  'en-AU': 'en-au',
  'en-CA': 'en-ca',
  'en-GB': 'en-gb',
  'en-US': 'en-us',
  'es-ES': 'es',
  'fr-FR': 'fr',
  'it-IT': 'it',
  'nl-NL': 'nl'
};

const requestLocale = (request) => {
  let locale;
  if (request.i18n && request.i18n.getLocale) {
    locale = request.i18n.getLocale();
  }
  return locale;
};

const getDecimalPlaces = (num) => {
  let match = ('' + num).match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
  // ('' + num).match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/) cannot return undefined. It will always run and return a value
  return Math.max(
    0,
    // Number of digits right of decimal point.
    (match[1] ? match[1].length : 0)
    // Adjust for scientific notation.
    - (match[2] ? +match[2] : 0)
  );
};

const formatPriceWithDecimal = (amount, cur, lang, decimalPlaces, hideDecimalsIfWholeNumber, customOptions) => {
  let currency = get(customOptions, 'currency', currencies[cur]);
  const customCurrencySeparator = get(customOptions, `formatingOptions.currencySeparator.${cur}`, '');
  if (!currency) {
    currency = currencies.USD;
  }

  const value = formatWithDecimal(amount, lang, decimalPlaces, null, hideDecimalsIfWholeNumber, get(customOptions, 'formatingOptions', null));
  if (value !== '') {
    if (get(currency, 'useCurrencyCode', false)) {
      const currentCurrency = currencies[cur] || currencies.USD;
      return currentCurrency.code + ' ' + value;
    }
    else if (get(customOptions,'formatingOptions.symbolOrder','') === 'prefix') {
      return currency.symbol + customCurrencySeparator + value;
    }
    else if (get(customOptions,'formatingOptions.symbolOrder','') === 'postfix'){
      return value + customCurrencySeparator + currency.symbol;
    }
    else if (currency.trailingSymbol) {
      return value + ' ' + currency.symbol;
    }
    return currency.symbol + value;
  }
  return '';
};

const formatWithDecimal = (amount, lang, decimalPlaces, hideOrderSeparator, hideDecimalsIfWholeNumber, customLocaleOptions) => {
  let locale = customLocaleOptions ? customLocaleOptions : locales[lang];
  if (!locale) {
    locale = locales['en-US'];
  }
  const orderSeparator = (hideOrderSeparator) ? '' : locale.orderSeparator;

  if (!isNaN(parseFloat(amount)) && isFinite(parseFloat(amount))) {
    amount = Number(amount);
    if (hideDecimalsIfWholeNumber && amount % 1 === 0) {
      decimalPlaces = 0;
    }
    return numberFormat(parseFloat(amount), decimalPlaces, locale.decimalSeparator, orderSeparator);
  }

  return '';
};

const formatPrice = (amount, cur, lang, customOptions = null) => {
  return formatPriceWithDecimal(amount, cur, lang, 0, null, customOptions);
};

const formatNumber = (amount, lang) => {
  return formatWithDecimal(amount, lang, 0);
};

const calculateTax = (amount, percentage, cur, lang) => {
  const taxAmount = percentage / 100 * amount;
  return formatPriceWithDecimal(taxAmount, cur, lang, 0);
};

const calculateNetAmount = (amount, percentage, cur, lang, decimalPlaces, hideDecimalsIfWholeNumber) => {
  const totalNetAmount = (1 - percentage / 100) * amount;
  return formatPriceWithDecimal(totalNetAmount, cur, lang, decimalPlaces, hideDecimalsIfWholeNumber);
};

const getLength = (request, callback) => {
  const formatLength = (length, formatNominal) => {
    if (isNaN(parseFloat(length)) || !isFinite(parseFloat(length))) {
      return '';
    }

    const conversionUom = get(locales[requestLocale(request)], 'lengthUom', uoms.ft);

    const translate = (val) => {
      const translator = get(request, 'i18n', {
        __: (arg) => {
          return arg === 'uom.ft' ? 'ft' : 'in';
        }
      });
      return translator.__(val);
    };

    let result;

    if (conversionUom.code === 'm' || formatNominal) {
      const roundedValue = (conversionUom.code === 'm') ? Math.round(length * 100) / 100 : Math.round(length);
      result = formatWithDecimal(roundedValue, requestLocale(request), getDecimalPlaces(roundedValue)) + ' ' + translate('uom.' + conversionUom.code);
    } else {
      const fractionValue = length % 1;
      const feetValue = length - fractionValue;
      const inchesValue = Math.round((fractionValue * 12));
      result = formatNumber(feetValue, requestLocale(request)) + ' ' + translate('uom.ft') + (inchesValue > 0 ? ' ' + inchesValue + ' ' + translate('uom.in') : '');
    }
    return result;
  };

  callback(formatLength);
};

const formatNumberForLocale = (locale, length, formatNominal) => {
  if (isNaN(parseFloat(length)) || !isFinite(parseFloat(length))) {
    return '';
  }
  return formatWithDecimal(length, locale, formatNominal ? 0 : getDecimalPlaces(length));
};

const capitalizeEachWord = (sentence) => {
  // capitalize() only caplitalizes the first word.
  // This code was copied from http://stackoverflow.com/questions/196972/convert-string-to-title-case-with-javascript/196991#196991
  return sentence.replace(/\w\S*/g, (txt) => { return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(); });
};

const normalizeString = (value, exemptedSpecialCharacters = null) => {
  if (value) {
    let result = value.toLowerCase();
    if (exemptedSpecialCharacters) {
      const regex = new RegExp(`[^a-zA-Z0-9${exemptedSpecialCharacters}]`, 'g');
      result = result.replace(regex, '-');
    } else {
      result = result.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      result = result.replace(/[^a-zA-Z0-9]+/g, '-');
    }
    result = result.replace(/(--)+/g, '-');
    result = result.replace(/\-$/, '');
    result = result.replace(/^\-/, '');
    return result;
  }
  return '';
};

const formatMultiArrayForURL = (array, separator) => {
  separator = separator || '|';
  return array.map( (item) => {
    return JSON.stringify(item);
  }).join(separator).replace(/\,\[\]/g, '');
};

const localeDate = (date, options) => {
  const thisOptions = defaults(options || {}, defaultLocale.defaults);
  const mDate = dayjs.utc(date);
  const mappedLocale = mappingLocales[thisOptions.locale] ? mappingLocales[thisOptions.locale] : 'en-US';
  return dayjs(mDate).locale(mappedLocale).format(thisOptions.dateFormat);
};

module.exports.formatNumberForLocale = formatNumberForLocale;
module.exports.formatPrice = formatPrice;
module.exports.calculateTax = calculateTax;
module.exports.calculateNetAmount = calculateNetAmount;
module.exports.formatPriceWithDecimal = formatPriceWithDecimal;
module.exports.formatLength = getLength;
module.exports.formatNumber = formatNumber;
module.exports.formatWithDecimal = formatWithDecimal;
module.exports.capitalizeEachWord = capitalizeEachWord;
module.exports.normalizeString = normalizeString;
module.exports.formatMultiArrayForURL = formatMultiArrayForURL;
module.exports.localeDate = localeDate;
module.exports.requestLocale = requestLocale;
module.exports.getDecimalPlaces = getDecimalPlaces;
